/**
 * Ряд подписанных полей строки `list`: пара «подпись → значение» и сборка ряда
 * вместе с парой решения. Выделено из `ListRowFrame.tsx` (лимит 350 строк) —
 * разметка полей самостоятельна и не делит ветвлений с головой и углами каркаса.
 */
import { ReactNode } from 'react';

import { Box, Typography } from '@mui/material';

import { passedSx } from '@/shared/lib/utils';

import { EMPTY_FIELD_VALUE } from '@/shared/constants';

import {
  DECISION_SLOT_SX,
  FRAME_FIELD_INLINE_SX,
  FRAME_FIELD_SX,
  IRowField,
  isFieldEmpty,
  LIST_FIELDS_SX,
  ROW_LABEL_SX,
  ROW_VALUE_EMPTY_SX,
  ROW_VALUE_SX,
  TIGHT_FIELD_INLINE_SX,
  TIGHT_FIELDS_GRID_SX,
  TIGHT_FIELDS_SX
} from './listRowFrameStyles';

/**
 * Пара «подпись → значение» строки `list`. Пустое значение не прячет пару, а
 * рисует приглушённую форму (по умолчанию «Не указано»): ряды списка обязаны
 * совпадать по высоте.
 *
 * Форма пустого значения — `field.emptyValue`, а не разбор `label` каркасом:
 * «Возраст»/«Город»/«Прием заявок» и подобные — мужского рода («Не указан»), «Роль» —
 * женского («Не указана»), и домен, которому подпись известна буквально,
 * объявляет форму сам (18976, Р6).
 *
 * Значение рендерится `component='div'`, а не Typography-дефолтным `<p>`:
 * `IRowField['value']` — `ReactNode`, и это не только текст (визитка кладёт в
 * поле «Статус» `Chip`, а тот рендерится `<div>`). `<div>` внутри `<p>` —
 * невалидная вложенность, и React печатает предупреждение в консоль; `span` не
 * годится по той же причине (блочный элемент внутри строчного тоже запрещён).
 * `div`, как и `p`, блочный по умолчанию — переключение тега визуально ничего
 * не меняет, только снимает невалидную вложенность.
 * @param props - пропсы
 * @param props.label - подпись поля
 * @param props.value - значение поля; пусто → `emptyValue`
 * @param props.emptyValue - форма пустого значения; дефолт — «Не указано»
 * @param props.inline - пара строкой и ниже WIDE_ROW (fieldsInline каркаса)
 * @param props.tight - ячейки таблицы плотного ряда (`TIGHT_FIELDS_GRID_SX`), только с `inline`
 */
const FrameField = ({
  label,
  value,
  emptyValue = EMPTY_FIELD_VALUE,
  inline = false,
  tight = false
}: IRowField & { inline?: boolean; tight?: boolean }) => {
  const empty = isFieldEmpty(value);
  const inlineSx = tight ? TIGHT_FIELD_INLINE_SX : FRAME_FIELD_INLINE_SX;

  return (
    <Box sx={inline ? inlineSx : FRAME_FIELD_SX}>
      <Typography sx={ROW_LABEL_SX}>{label}</Typography>
      <Typography
        component='div'
        sx={empty ? ROW_VALUE_EMPTY_SX : ROW_VALUE_SX}
      >
        {empty ? emptyValue : value}
      </Typography>
    </Box>
  );
};

/**
 * Стили ряда полей: базовый, плотный ритм и таблица плотных пар в строку
 * @param fieldsInline - пары строкой и на узкой строке
 * @param tight - плотный ритм (`TIGHT_FIELDS_SX`)
 */
const getFieldsRowSx = (fieldsInline: boolean, tight: boolean) => {
  if (!tight) return passedSx(LIST_FIELDS_SX);

  return [
    ...passedSx(LIST_FIELDS_SX),
    ...passedSx(TIGHT_FIELDS_SX),
    ...(fieldsInline ? passedSx(TIGHT_FIELDS_GRID_SX) : [])
  ];
};

/**
 * Ряд трёх подписанных полей `list` вместе с парой решения — она последний
 * элемент ТОГО ЖЕ ряда (18976, Р73, см. `DECISION_SLOT_SX`), а не отдельная
 * полоса. Ряд не рисуется, только когда нет ни полей, ни пары: пустой
 * `LIST_FIELDS_SX` добавил бы строке `mt` на пустом месте. Вынесено из
 * `RowBody` по той же причине, что и `buildListBadgesRow`.
 * @param fields - РОВНО три поля `list`
 * @param decisionSlot - пара решения («Отклонить»/«Принять»), если она есть
 * @param fieldsInline - пары строкой и на узкой строке
 * @param tight - плотный ритм (`TIGHT_FIELDS_SX`)
 */
export const buildFieldsRow = (
  fields?: IRowField[],
  decisionSlot?: ReactNode,
  fieldsInline = false,
  tight = false
): ReactNode => {
  if (!fields?.length && !decisionSlot) {
    return null;
  }

  return (
    <Box sx={getFieldsRowSx(fieldsInline, tight)} data-testid='list-row-fields'>
      {fields?.map(field => (
        <FrameField
          key={field.label}
          inline={fieldsInline}
          tight={tight}
          {...field}
        />
      ))}
      {!!decisionSlot && (
        <Box sx={DECISION_SLOT_SX} data-testid='list-row-decision'>
          {decisionSlot}
        </Box>
      )}
    </Box>
  );
};
